import React, { useEffect, useState } from "react"
import { Button, Toast, ToastContainer } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import ScaleLoader from "react-spinners/ScaleLoader"
import { ErrorObject, pricing } from "../../api"
import { BulkUpdateShippingRateRequest } from "../../api/pricing.api"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import CheckCircleIcon from "../../assets/checkCircleIcon.svg"
import { RootState } from "../../store"
import {
    bulkUpdateShippingRate,
    loadShippingRates,
    loadSurcharges,
    resetSuccess,
    updateShippingRate
} from "../../store/pricing.store"
import Header from "../Layout/Header"
import BulkUpdateModal from "./BulkUpdateModal"
import BulkUploadModal from "./BulkUploadModal"
import ShippingRateModal from "./ShippingRateModal"
// import DateRangePickerCustom from "components/Common/DateRangePicker/DateRangePickerCustom";
import ShippingRatesTable from "./ShippingRatesTable"
const ShippingRates = () => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const { userInfo, defaultWarehouse } = useAppSelector((state: RootState) => state.auth)
    const { shippingRates, loading, error, success } = useAppSelector((state: RootState) => state.pricing)
    const { selectedWarehouse } = useAppSelector((state: RootState) => state.warehouses)

    const [shippingRateDetails, setShippingRateDetails] = useState({} as Record<string, any>)
    const [show, setShow] = useState("")
    const [errors, setErrors] = useState({} as Record<string, any>)
    const [shippingRateSearch, setShippingRateSearch] = useState({
        country: "",
        shipping_company_id: "",
        shipping_name: "",
        date: "",
        shipping_service: ""
    })
    const [selectedRates, setSelectedRates] = useState([] as number[])
    const [isFiltered, setIsFiltered] = useState("")
    const [uploadedFile, setUploadedFile] = useState()
    useEffect(() => {
        if (selectedWarehouse || defaultWarehouse)
            dispatch(
                loadShippingRates({
                    warehouse: selectedWarehouse?.toString() || defaultWarehouse?.toString(),
                    country: shippingRateSearch?.country,
                    shippingCompany: shippingRateSearch?.shipping_company_id,
                    shippingService: shippingRateSearch?.shipping_service
                })
            )
    }, [JSON.stringify(shippingRateSearch)])

    const findFormErrors = (customError?: Record<string, any>) => {
        let newErrors = {} as Record<string, any>
        if (!customError) {
            if (error?.payload && error?.payload?.length !== 0) {
                error?.payload?.forEach((e: ErrorObject) => (newErrors[e?.param?.split(".")[1]] = e.msg))
            } else if (error?.error && error?.error !== "") {
                newErrors["genericError"] = error.error
            }
        } else {
            newErrors[customError?.param] = customError?.msg
        }
        return newErrors
    }

    const handleSubmit = async (shippingRateDetails: Record<string, any>) => {
        if (shippingRateDetails?.id) {
            setShippingRateSearch({
                country: "",
                shipping_company_id: "",
                shipping_name: "",
                date: "",
                shipping_service: ""
            })
            await dispatch(
                updateShippingRate({
                    existingShippingRateData: shippingRateDetails,
                    warehouse: (selectedWarehouse || defaultWarehouse).toString()
                })
            )
        }
    }

    const handleUpload = async () => {
        try {
            const res = await pricing.bulkUploadUpdate(uploadedFile, selectedWarehouse || defaultWarehouse)
            if (res?.data.status) {
                dispatch(
                    loadShippingRates({
                        warehouse: selectedWarehouse?.toString() || defaultWarehouse?.toString(),
                        country: shippingRateSearch?.country,
                        shippingCompany: shippingRateSearch?.shipping_company_id
                    })
                )
                dispatch(loadSurcharges({}))
            }
        } catch (error) {
            console.log(error)
        }
    }

    const handleUpdateBulkRates = async (data: BulkUpdateShippingRateRequest) => {
        const rates = [...(isFiltered === "filtered" ? shippingRates?.map((sr) => sr?.id) : selectedRates)]
        if (rates?.length > 0) {
            await dispatch(
                bulkUpdateShippingRate({
                    bulkRatesData: {
                        ...data,
                        ids: rates,
                        filters:
                            isFiltered === "filtered"
                                ? {
                                      warehouse_id: selectedWarehouse || defaultWarehouse,
                                      country_code: shippingRateSearch?.country,
                                      shipping_company_id: Number(shippingRateSearch?.shipping_company_id),
                                      shipping_service: shippingRateSearch?.shipping_service
                                  }
                                : undefined
                    },
                    type: isFiltered
                })
            )
            setShippingRateSearch({
                country: "",
                shipping_company_id: "",
                shipping_name: "",
                date: "",
                shipping_service: ""
            })
        }
    }

    useEffect(() => {
        const returnedErrors = findFormErrors()
        setErrors(returnedErrors)
    }, [error?.error, error?.payload?.length])

    useEffect(() => {
        if (success) {
            dispatch(
                loadShippingRates({
                    warehouse: selectedWarehouse?.toString() || defaultWarehouse?.toString()
                })
            )
            setSelectedRates([])
            setIsFiltered("")
            setShow("")
            dispatch(resetSuccess())
        }
    }, [success])

    useEffect(() => {
        if (isFiltered === "filtered") {
            if (selectedRates?.length > 0) {
                if (document?.getElementsByClassName("selection-cell-header")?.length > 0) {
                    const selectAllCheckBox =
                        (document?.getElementsByClassName("selection-cell-header")[0]
                            ?.children[0] as HTMLElement) || null
                    if (selectAllCheckBox) {
                        selectAllCheckBox?.click()
                    }
                }
            }
            setSelectedRates([])
        } else if (isFiltered === "selected") {
            if (selectedRates?.length === 0) {
                if (document?.getElementsByClassName("selection-cell-header")?.length > 0) {
                    const selectAllCheckBox =
                        (document?.getElementsByClassName("selection-cell-header")[0]
                            ?.children[0] as HTMLElement) || null
                    if (selectAllCheckBox) {
                        selectAllCheckBox?.click()
                    }
                }
            }
        }
    }, [isFiltered])

    return (
        <>
            {(!error && loading) || !userInfo?.accessToken ? (
                <div className="loader row justify-content-center">
                    <ScaleLoader color="#15397F" width={4} height={40} />
                </div>
            ) : (
                <div className="pricing-container">
                    {success && (
                        <ToastContainer className="p-3" position={"top-end"}>
                            <Toast onClose={() => dispatch(resetSuccess())}>
                                <Toast.Header closeButton className="bg-light-green border-bottom-0">
                                    <div className="mr-auto d-flex align-items-center">
                                        <img src={CheckCircleIcon} />
                                        <strong className="mx-2 text-black">Success</strong>
                                        <span className="text-black">{t("Changes are saved")}</span>
                                    </div>
                                </Toast.Header>
                            </Toast>
                        </ToastContainer>
                    )}
                    <Header
                        title={t("Pricing / Shipping Rates")}
                        records={shippingRates?.length}
                        data={shippingRates}
                        name="ShippingRates">
                        <div className="mx-2">
                            <Button className="primary-bg" onClick={() => setShow("bulkUploadModal")}>
                                {t("Bulk Rates")}
                            </Button>
                            {/*<DateRangePickerCustom setStartDate={setStartDate} setEndDate={setEndDate} />*/}
                        </div>
                    </Header>

                    <div className="row  d-flex">
                        <ShippingRatesTable
                            shippingRates={shippingRates}
                            shippingRateDetails={shippingRateDetails}
                            setShippingRateDetails={setShippingRateDetails}
                            setShow={setShow}
                            shippingRateSearch={shippingRateSearch}
                            setShippingRateSearch={setShippingRateSearch}
                            setSelectedRates={setSelectedRates}
                            selectedRates={selectedRates}
                            isFiltered={isFiltered}
                            setIsFiltered={setIsFiltered}
                        />
                    </div>
                    <ShippingRateModal
                        loading={loading}
                        show={show}
                        setShow={setShow}
                        shippingRateDetails={shippingRateDetails}
                        setShippingRateDetails={setShippingRateDetails}
                        handleClose={() => {
                            setShippingRateDetails({})
                            setShow("")
                        }}
                        handleSubmit={handleSubmit}
                    />

                    <BulkUploadModal
                        loading={loading}
                        show={show}
                        setShow={setShow}
                        handleClose={() => setShow("")}
                        handleSubmit={handleUpload}
                        setUploadedFile={setUploadedFile}
                    />

                    <BulkUpdateModal
                        loading={loading}
                        show={show}
                        setShow={setShow}
                        handleClose={() => {
                            setShow("")
                            setSelectedRates([])
                        }}
                        shippingRates={
                            isFiltered === "filtered"
                                ? shippingRates
                                : shippingRates?.filter((sr: Record<string, any>) =>
                                      selectedRates.includes(sr?.id)
                                  )
                        }
                        handleSubmit={handleUpdateBulkRates}
                    />
                </div>
            )}
        </>
    )
}

export default ShippingRates
